
import { useState, useEffect } from 'react';
import {
  Facebook,
  CheckCircle,
  ArrowRight,
  ExternalLink,
  Shield,
  Plus,
  Trash2,
  Edit,
  Calendar,
  Target,
  BarChart3
} from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';

import { getClientIntegrations, postFacebookPixelIntegration, deleteClientIntegration, Integration } from '@/actions/data-fetch-settings';
import InputField from '../../../components/common/InputField/InputField';
import Button from '../../../components/common/Button';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toastMessage } from '@/utils/toast-message';

const schema = yup.object({
  pixel_code: yup.string()
    .required('Facebook Pixel ID is required')
    .matches(/^[0-9]{15,20}$/, 'Must be 15-20 digits')
    .min(15, 'Must be at least 15 digits')
    .max(20, 'Must be at most 20 digits')
}).required();

type FormData = {
  pixel_code: string;
};

const FacebookPixelIntegrationPage = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      pixel_code: ''
    }
  });

  // Fetch existing integrations
  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await getClientIntegrations();
      if (response.success && response.data) {
        // Filter only Facebook Pixel integrations (type 8)
        const facebookPixelIntegrations = response.data.integrations.filter(
          integration => integration.type === 8
        );
        setIntegrations(facebookPixelIntegrations);
      }
    } catch (error: any) {
      console.error('Error fetching integrations:', error);
      toastMessage('Error fetching integrations', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const onSubmit = async (data: FormData) => {
    try {
      setSubmitting(true);
      const params = {
        pixel_code: data.pixel_code,
      };
      const response = await postFacebookPixelIntegration(params);
      if (response.success) {
        toastMessage(response.message || 'Facebook Pixel integration added successfully', 'success');
        reset({ pixel_code: '' });
        // Refresh integrations list
        await fetchIntegrations();
      } else {
        throw new Error(response.message || 'Failed to add Facebook Pixel integration');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Please try again later', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIntegration = async (integrationUuid: string) => {
    try {
      setDeletingId(integrationUuid);
      const response = await deleteClientIntegration(integrationUuid);
      if (response.success) {
        toastMessage('Facebook Pixel integration deleted successfully', 'success');
        // Refresh integrations list
        await fetchIntegrations();
      } else {
        throw new Error(response.message || 'Failed to delete integration');
      }
    } catch (error: any) {
      toastMessage(error.message || 'Failed to delete integration', 'error');
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm(null);
    }
  };

  const confirmDelete = (integrationUuid: string) => {
    setShowDeleteConfirm(integrationUuid);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const features = [
    {
      icon: <Target className="w-6 h-6 text-blue-600" />,
      title: "Conversion Tracking",
      description: "Track website conversions and optimize ad performance"
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-green-600" />,
      title: "Analytics & Insights",
      description: "Get detailed insights about your website visitors"
    },
    {
      icon: <Shield className="w-6 h-6 text-purple-600" />,
      title: "Secure Integration",
      description: "Protected connection with Facebook's advertising platform"
    }
  ];

  const setupSteps = [
    {
      step: 1,
      title: "Access Facebook Ads Manager",
      description: "Go to Facebook Ads Manager and navigate to Events Manager"
    },
    {
      step: 2,
      title: "Find Your Pixel ID",
      description: "Locate your Facebook Pixel and copy the Pixel ID (15-20 digits)"
    },
    {
      step: 3,
      title: "Add Pixel ID",
      description: "Enter your Pixel ID in the form below to complete integration"
    },
    {
      step: 4,
      title: "Test Your Pixel",
      description: "Use Facebook's Test Events tool to verify tracking is working"
    }
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasExistingIntegration = integrations.length > 0;

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Facebook Pixel Integration"
        description="Connect your Facebook Pixel to track conversions and optimize your advertising campaigns"
        icon={<Facebook className="h-5 w-5" />}
      />

      <div className="p-6">
        {/* Features Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Integration Benefits
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {feature.icon}
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {feature.title}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-24 h-24 border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-brandColor/10 dark:bg-brandColor/20">
              <Facebook className="w-full h-full text-brandColor dark:text-brandColor" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Setup Instructions
            </h3>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
              <p>Follow these steps to integrate your Facebook Pixel with ScaleDino CRM:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {setupSteps.map((step, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 bg-brandColor/10 dark:bg-brandColor/20 rounded-full flex items-center justify-center">
                        <span className="text-xs font-semibold text-brandColor dark:text-brandColor">{step.step}</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {step.title}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Integration Form */}
      {!hasExistingIntegration && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Add Facebook Pixel Integration
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                type="text"
                id="pixel-code"
                name="pixel_code"
                placeholder="Enter your Facebook Pixel ID (15-20 digits)"
                label="Facebook Pixel ID"
                control={control}
                error={errors.pixel_code?.message}
              />
              <div className="flex items-end">
                <Button
                  type="submit"
                  btnStyle="primary"
                  className="w-full h-[42px]"
                  disabled={submitting}
                >
                  <div className="inline-flex items-center justify-center whitespace-nowrap">
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 flex-shrink-0"></div>
                        <span>Adding...</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>Add Integration</span>
                      </>
                    )}
                  </div>
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Current Integrations */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Current Integration
        </h3>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading integration...</span>
          </div>
        ) : integrations.length === 0 ? (
          <div className="text-center py-8">
            <Facebook className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No Facebook Pixel integration found.</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Add your Facebook Pixel ID to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {integrations.map((integration) => (
              <div
                key={integration.uuid}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-brandColor/10 dark:bg-brandColor/20 rounded-full flex items-center justify-center">
                      <Facebook className="w-5 h-5 text-brandColor dark:text-brandColor" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Pixel ID: {integration.code}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Created: {formatDate(integration.created_at)}
                      </span>
                      {integration.updated_at !== integration.created_at && (
                        <span className="flex items-center">
                          <Edit className="w-4 h-4 mr-1" />
                          Updated: {formatDate(integration.updated_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Active
                  </span>
                  <button
                    onClick={() => confirmDelete(integration.uuid)}
                    disabled={deletingId === integration.uuid}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete integration"
                  >
                    {deletingId === integration.uuid ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pixel Implementation Code */}
      {hasExistingIntegration && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Implementation Code
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Add this code to your website's &lt;head&gt; section to activate Facebook Pixel tracking:
          </p>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
            <pre>{`<!-- Facebook Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');

fbq('init', '${integrations[0]?.code}');
fbq('track', 'PageView');
</script>
<noscript>
  <img height="1" width="1" style="display:none"
    src="https://www.facebook.com/tr?id=${integrations[0]?.code}&ev=PageView&noscript=1"
  />
</noscript>
<!-- End Facebook Pixel Code -->`}</pre>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-brandColor/10 dark:bg-brandColor/20 border border-brandColor/30 dark:border-brandColor/40 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-brandColor dark:text-brandColor mb-3">
          Need Help?
        </h3>
        <div className="space-y-2 text-sm text-brandColor dark:text-brandColor">
          <div className="flex items-start space-x-2">
            <ExternalLink className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Visit Facebook Ads Manager → Events Manager to find your Pixel ID</span>
          </div>
          <div className="flex items-start space-x-2">
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Use Facebook's Test Events tool to verify your pixel is working correctly</span>
          </div>
          <div className="flex items-start space-x-2">
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>You can only have one Facebook Pixel integration active at a time</span>
          </div>
          <div className="flex items-start space-x-2">
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Contact support if you need help with pixel setup or conversion tracking</span>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Delete Integration
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Are you sure you want to delete this Facebook Pixel integration? 
                You will stop tracking conversions and analytics data.
              </p>
              <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Pixel ID: {integrations.find(i => i.uuid === showDeleteConfirm)?.code}
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                btnStyle="secondary"
                onClick={cancelDelete}
                disabled={deletingId === showDeleteConfirm}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                type="button"
                btnStyle="redBtn"
                onClick={() => handleDeleteIntegration(showDeleteConfirm)}
                disabled={deletingId === showDeleteConfirm}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white"
              >
                <div className="inline-flex items-center justify-center whitespace-nowrap">
                  {deletingId === showDeleteConfirm ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 flex-shrink-0"></div>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>Delete Integration</span>
                    </>
                  )}
                </div>
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default FacebookPixelIntegrationPage;